import type { AutoCombineEntryV2, EntryMiniTransaction } from "@/shared/cache";

export type EntryGroupSelectionSource = {
	parent: AutoCombineEntryV2;
	mini: EntryMiniTransaction;
};

export type NewEntryGroupFields = {
	clientId?: string | null;
	matterId?: string | null;
	taskCodeId?: string | null;
	activityCodeId?: string | null;
};

function commonNonEmptyString(
	values: ReadonlyArray<string | null | undefined>,
): string | null {
	if (values.length === 0) return null;
	const normalized = values.map((value) => value ?? null);
	const first = normalized[0];
	if (!first) return null;
	return normalized.every((value) => value === first) ? first : null;
}

export function resolveNewEntryGroupFields(
	sources: ReadonlyArray<EntryGroupSelectionSource>,
): NewEntryGroupFields {
	const matterId = commonNonEmptyString(
		sources.map(({ mini, parent }) => mini.matterId ?? parent.matter?.id),
	);
	const clientId = matterId
		? commonNonEmptyString(
				sources.map(
					({ mini, parent }) => mini.clientId ?? parent.matter?.client,
				),
			)
		: null;

	return {
		clientId,
		matterId,
		taskCodeId: commonNonEmptyString(
			sources.map(({ parent }) => parent.codes?.taskId),
		),
		activityCodeId: commonNonEmptyString(
			sources.map(({ parent }) => parent.codes?.activityId),
		),
	};
}

export function uniqueSelectedSourceIds(
	sources: ReadonlyArray<EntryGroupSelectionSource>,
): string[] {
	return [...new Set(sources.map(({ mini }) => mini.id))];
}

export function uniqueSelectedParents(
	sources: ReadonlyArray<EntryGroupSelectionSource>,
): AutoCombineEntryV2[] {
	const seen = new Set<string>();
	const parents: AutoCombineEntryV2[] = [];
	for (const { parent } of sources) {
		if (seen.has(parent.id)) continue;
		seen.add(parent.id);
		parents.push(parent);
	}
	return parents;
}
